import type { AugmentedAccount, AugmentedUser } from '@/services/auth/types';
import * as Sentry from '@sentry/react';
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/react';

import { getEmbeddedAppLoadDiagnostics, sanitizeAppLoadDiagnosticPath } from './sentry-app-load-diagnostics';
export { getEmbeddedAppLoadDiagnostics, sanitizeAppLoadDiagnosticPath } from './sentry-app-load-diagnostics';

const APP_LOAD_DIAGNOSTIC_STORAGE_PREFIX = 'hydra:sentry-app-load-diagnostic';
const DEFAULT_APP_LOAD_DIAGNOSTIC_RATE_LIMIT_MS = 10 * 60 * 1000;

const appLoadDiagnosticMemoryCache = new Map<string, number>();

const ignoreErrors = [
  // Ignore cross-origin frame messages, almost definitely from adblock
  `Failed to read the 'contentDocument' property from 'HTMLIFrameElement'`,

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Dead_object
  // Some customers may use browser addons to perform unsupported activities -
  // Firefox in particular has this special TypeError that may spam us with
  // unnecessary Sentry calls if not silenced
  `can't access dead object`,

  // Parcel (embedded email editor)
  `Cannot read properties of undefined (reading 'getModel')`,
  'setVariables timed out after 10000ms',
  /t.monaco.editor/,

  // Ignore fetch errors, since they're ambiguous and don't provide any helpful detail
  // related issue: https://github.com/getsentry/sentry-javascript/issues/4146
  /Failed to fetch/,
  /Load failed/,
  /AxiosError/,
  /Unable to preload CSS/,
  /APIError/,

  // AbortError fires when the browser cancels in-flight fetches during navigation.
  // This is expected SPA behaviour — no stack trace, not actionable.
  /AbortError/,

  // Ember errors, the ember sentry integration handles this
  /VM BUG/,
  /Ember Data Request/,
];

function shouldSentryWork() {
  return ['standalone', 'prod-staging-1', 'prod-staging-2', 'production'].includes(__CIO_ENV__);
}

function isRecentlyCaptured(key: string, rateLimitMs: number) {
  const now = Date.now();

  try {
    const lastCapturedAt = Number(window.localStorage.getItem(key));
    if (Number.isFinite(lastCapturedAt) && now - lastCapturedAt < rateLimitMs) {
      return true;
    }

    window.localStorage.setItem(key, String(now));
    return false;
  } catch {
    const lastCapturedAt = appLoadDiagnosticMemoryCache.get(key);
    if (lastCapturedAt != null && now - lastCapturedAt < rateLimitMs) {
      return true;
    }

    appLoadDiagnosticMemoryCache.set(key, now);
    return false;
  }
}

type AppLoadDiagnosticOptions = {
  dedupeKey?: string[];
  fingerprint?: string[];
  level?: NonNullable<Breadcrumb['level']>;
  rateLimitMs?: number;
};

export function sentryAddAppLoadBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
  level: NonNullable<Breadcrumb['level']> = 'info',
) {
  if (!shouldSentryWork()) {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'embedded-app-load',
    level,
    message,
    data,
  });
}

export function sentryCaptureAppLoadDiagnostic(
  kind: string,
  context: Record<string, unknown>,
  options: AppLoadDiagnosticOptions = {},
) {
  if (!shouldSentryWork()) {
    return;
  }

  const dedupeKey = [
    APP_LOAD_DIAGNOSTIC_STORAGE_PREFIX,
    kind,
    ...(options.dedupeKey ?? [String(context.appBundle ?? 'unknown')]),
  ].join(':');
  if (isRecentlyCaptured(dedupeKey, options.rateLimitMs ?? DEFAULT_APP_LOAD_DIAGNOSTIC_RATE_LIMIT_MS)) {
    return;
  }

  const appBundle = typeof context.appBundle === 'string' ? context.appBundle : 'unknown';
  const sanitizedContext = {
    ...context,
    activeRoutePath:
      typeof context.activeRoutePath === 'string'
        ? sanitizeAppLoadDiagnosticPath(context.activeRoutePath)
        : context.activeRoutePath,
  };

  Sentry.withScope((scope) => {
    scope.setLevel(options.level ?? 'warning');
    scope.setTag('diagnostic', 'embedded_app_load');
    scope.setTag('diagnostic_kind', kind);
    scope.setTag('app_bundle', appBundle);
    scope.setFingerprint(options.fingerprint ?? ['hydra-embedded-app-load', kind, appBundle]);
    scope.setContext('embedded_app_load', {
      kind,
      path: sanitizeAppLoadDiagnosticPath(window.location.pathname),
      searchPresent: window.location.search !== '',
      visibilityState: document.visibilityState,
      online: window.navigator.onLine,
      ...sanitizedContext,
      ...getEmbeddedAppLoadDiagnostics(),
    });

    Sentry.captureMessage(`Hydra embedded app load diagnostic: ${kind}`);
  });
}

function beforeBreadcrumb(breadcrumb: Breadcrumb) {
  if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
    return null;
  }

  return breadcrumb;
}

function formatNonErrorException(error: unknown): {
  formattedError: Record<string, unknown>;
  stringRepresentation: string;
} {
  try {
    // Returning an object to preserve the structure of the original error
    const formattedError: Record<string, unknown> =
      typeof error === 'string' ? { message: error } : JSON.parse(JSON.stringify(error));

    const stringRepresentation = String(error);
    return {
      formattedError,
      stringRepresentation,
    };
  } catch {
    return {
      formattedError: {
        message: 'Could not process non-Error exception',
        originalError: String(error),
      },
      stringRepresentation: String(error),
    };
  }
}

// fallow-ignore-next-line complexity
function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const error = hint.originalException;

  // filter out UnhandledRejection errors that have no information
  // example error: https://sentry.io/organizations/customerio/issues/2475399114/
  // github issue: https://github.com/getsentry/sentry-javascript/issues/3440
  if (event?.exception?.values?.length === 1) {
    const [exception] = event.exception.values;
    if (
      exception.type === 'UnhandledRejection' &&
      exception.value?.includes('Non-Error promise rejection captured with value: Object Not Found Matching Id:') ===
        true
    ) {
      return null;
    }
  }

  // If Fullstory is running, send the event to Fullstory too.
  if (error instanceof Error && window.FS?.event != null) {
    // Working production code accesses some untyped "error" properties
    // Define custom type with them and cast "error" to it so it type-checks correctly:
    const errorTyped = error as Error & { fileName: string; lineNumber: string };

    window.FS.event('Application Error', {
      name: errorTyped.name,
      message: errorTyped.message,
      fileName: errorTyped.fileName,
      lineNumber: errorTyped.lineNumber,
      stack: errorTyped.stack,
      sentryEventId: hint.event_id,
    });
  }

  // Serialize non-Error exceptions and attach to "extra" aka "additional data" field
  if (error != null && !(error instanceof Error)) {
    const { formattedError, stringRepresentation } = formatNonErrorException(error);
    event.extra = event.extra ?? {};
    event.extra.nonErrorException = formattedError;
    event.extra.nonErrorExceptionString = stringRepresentation;
  }

  return event;
}

export function setupSentry() {
  if (!shouldSentryWork()) {
    return;
  }

  // Only enable browser tracing conditionally while we sort out some
  // CORS issues on the backend. Should be removed once it's fixed.
  let isBrowserTracingEnabled = false;

  try {
    isBrowserTracingEnabled = localStorage.getItem('hydra:sentryBrowserTracing') === 'true';
  } catch {
    // localStorage might fail under some circumstances, just ignore it
  }

  Sentry.init({
    dsn: 'https://de97274fa5d848cca91d899b8df33337@o38230.ingest.sentry.io/4505875619905536',
    integrations: [
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [
          [405, 408],
          [410, 421],
          [423, 428],
          [430, 499],
          [503, 599],
        ],
      }),
      ...(isBrowserTracingEnabled ? [Sentry.browserTracingIntegration()] : []),
    ],

    tracesSampleRate: isBrowserTracingEnabled ? 1 : 0,
    // Prevents CORS issues. Can be removed once/if we adjust the backend
    // https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/#tracePropagationTargets
    tracePropagationTargets: [],

    environment: __CIO_ENV__,
    release: `hydra-ui@${__HYDRA__APP__VERSION__}+${__COMMIT_HASH__}+${__CIO_ENV__}`,
    ignoreErrors,
    beforeBreadcrumb,
    beforeSend,
    denyUrls: [
      // Filter out extension scripts
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
    // Only capture errors thrown from scripts loaded from our CDN or our domain
    allowUrls: [/https:\/\/(unityassets|fly)\.customer\.io/],
  });
}

// Sentry does come with a native FullStory integration: https://github.com/getsentry/sentry-fullstory
// but that requires using FullStory's SDK: https://github.com/fullstorydev/fullstory-browser-sdk
// We don't currently do that - we inject FullStory via our "tracking-scripts"
// utilities, so we need to set up FullStory manually.
// If/when we move to the FullStory SDK, we can also use the Sentry/FullStory
// integration.
export function setSentryFullStorySessionUrl() {
  window._fs_ready = function () {
    if (window?.FS?.getCurrentSessionURL != null) {
      const scope = Sentry.getCurrentScope();
      // The first argument to getCurrentSessionURL (true) ensures that we're getting the exact moment in time.
      scope.setExtra('fs_session_url', window.FS.getCurrentSessionURL(true));
    }
  };
}

export function setSentryMasqueradingContext(isMasqOn: boolean) {
  if (!shouldSentryWork()) {
    return;
  }

  Sentry.setExtra('masquerading_enabled', isMasqOn);
}

export function sentrySetDataCenter(dataCenter: string) {
  Sentry.getCurrentScope().setTag('data_center', dataCenter);
}

export function sentrySetUser(account: AugmentedAccount, user: AugmentedUser) {
  Sentry.setUser({ id: user.id, email: user.email, account_id: account.id });
}

export function sentryCaptureLegacyRedirect(path: string) {
  // Disabling temporarily, since 1: we are not planning to handle these legacy redirects in the near future,
  // 2: we have collected a lot of data on these redirects already and 3: this is spamming our logs

  // Not removing this altogether yet though because we might want to re-enable it in the future.
  // For more info on why this was added, https://github.com/customerio/hydra/pull/2054
  return;

  Sentry.withScope((scope) => {
    scope.setExtra('referrer', document.referrer);
    Sentry.captureMessage(`Legacy redirect used: ${path}`, scope);
  });
}

export const sentryCaptureException = Sentry.captureException;
